import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { CallbackModal } from '../components/CallbackModal';
import { MapPin, Star, Scan, Languages, Calendar, MessageCircle, CheckCircle2, Plus, Minus, Microscope, ShieldCheck, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
const TELEGRAM_URL = 'https://t.me/dentico_clinic';
function TelegramIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="currentColor" className={className} data-id="element-3436">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-3437" />
    </svg>;
}
function AccordionItem({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-3438">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-4 text-left focus:outline-none group" data-id="element-3439">
        <span className="text-lg font-medium text-gray-900 group-hover:text-forest-700 transition-colors" data-id="element-3440">
          {question}
        </span>
        <span className={`flex-shrink-0 ml-4 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-forest-600 text-white' : 'bg-stone-100 text-gray-400'}`} data-id="element-3441">
          {isOpen ? <Minus className="w-4 h-4" data-id="element-3442" /> : <Plus className="w-4 h-4" data-id="element-3443" />}
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`} data-id="element-3444">
        <p className="text-gray-600 leading-relaxed" data-id="element-3445">{answer}</p>
      </div>
    </div>;
}
export function RootCanalLondonRuPage() {
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const faqs = [{
    q: 'Когда нужно лечить каналы?',
    a: 'При воспалении нерва (пульпит) или инфекции в корне (периодонтит). Симптомы: сильная боль, чувствительность к горячему/холодному, отек десны, боль при накусывании или ночная боль.'
  }, {
    q: 'Больно ли лечить каналы?',
    a: 'Нет. Лечение проводится под эффективной местной анестезией, поэтому сама процедура безболезненна. После лечения возможен временный дискомфорт в течение нескольких дней.'
  }, {
    q: 'Сколько стоит лечение каналов в Лондоне?',
    a: 'Стоимость зависит от количества каналов в зубе и сложности случая. После осмотра и рентгена мы предоставляем точный план лечения и смету.'
  }, {
    q: 'Сколько визитов нужно?',
    a: 'Иногда лечение можно завершить за 1 визит. В более сложных случаях или при наличии инфекции может потребоваться 2 визита. Это определяется после диагностики.'
  }, {
    q: 'Нужна ли коронка после лечения каналов?',
    a: 'В большинстве случаев да, особенно для жевательных зубов. Зуб после удаления нерва становится хрупким, и коронка защищает его от раскола и продлевает срок службы.'
  }, {
    q: 'Можно ли спасти зуб, если сильно болит?',
    a: 'В большинстве случаев да. Современная эндодонтия позволяет спасти даже сильно разрушенные или инфицированные зубы. Чем раньше вы обратитесь, тем больше шансов сохранить зуб.'
  }, {
    q: 'Делаете ли вы срочные визиты при острой боли?',
    a: 'Да, мы понимаем, что зубную боль нельзя терпеть. Свяжитесь с нами как можно скорее — мы постараемся найти ближайшее доступное время.'
  }, {
    q: 'Где находится клиника?',
    a: '360–364 City Road, London EC1V 2PY, рядом с Angel Station (Islington).'
  }];
  // Schemas
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a
      }
    }))
  };
  const procedureSchema = {
    '@context': 'https://schema.org',
    '@type': 'MedicalProcedure',
    name: 'Root Canal Treatment',
    description: 'Endodontic treatment to save a tooth that is badly decayed or infected.',
    procedureType: 'http://schema.org/SurgicalProcedure',
    provider: {
      '@type': 'Dentist',
      name: 'Dentico Clinic',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '360-364 City Road',
        addressLocality: 'London',
        postalCode: 'EC1V 2PY',
        addressCountry: 'GB'
      }
    }
  };
  return <Layout data-id="element-3446">
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-3447" />

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} data-id="element-3448" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(procedureSchema)
    }} data-id="element-3449" />

      {/* 1. HERO SECTION */}
      <section className="relative bg-[#F8F5F0] overflow-hidden" data-id="element-3450">
        <div className="absolute inset-0 bg-[#EFE8DE] -skew-y-3 origin-top-left transform translate-y-20" data-id="element-3451" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 lg:pt-20 lg:pb-24 relative" data-id="element-3452">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-3453">
            <div className="space-y-6" data-id="element-3454">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#EFE8DE] text-[#2F4A3F] border border-[#D8C4A8] text-sm font-medium" data-id="element-3455">
                <span className="relative flex h-2 w-2" data-id="element-3456">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-forest-600 opacity-75" data-id="element-3457"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-forest-700" data-id="element-3458"></span>
                </span>
                Эндодонтия (лечение каналов)
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2B2D2F] leading-tight tracking-tight" data-id="element-3459">
                Лечение каналов в Лондоне –{' '}
                <span className="text-forest-700" data-id="element-3460">Dentico Clinic</span>
                <br data-id="element-3461" />
                <span className="text-2xl sm:text-3xl font-medium text-stone-500 mt-2 block" data-id="element-3462">
                  (Angel, Islington)
                </span>
              </h1>

              <p className="text-lg text-gray-600 max-w-lg leading-relaxed" data-id="element-3463">
                Безболезненное лечение корневых каналов под микроскопом. Спасаем
                зубы от удаления. Консультация на русском языке.
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-3 text-sm font-medium text-[#2F4A3F]" data-id="element-3464">
                <div className="flex items-center gap-1.5 bg-[#F8F5F0] px-3 py-1.5 rounded-lg border border-[#D8C4A8] shadow-sm" data-id="element-3465">
                  <Microscope className="w-4 h-4 text-[#2F4A3F]" data-id="element-3466" />
                  Лечение под микроскопом
                </div>
                <div className="flex items-center gap-1.5 bg-[#F8F5F0] px-3 py-1.5 rounded-lg border border-[#D8C4A8] shadow-sm" data-id="element-3467">
                  <Zap className="w-4 h-4 text-yellow-500" data-id="element-3468" />
                  Без боли
                </div>
                <div className="flex items-center gap-1.5 bg-[#F8F5F0] px-3 py-1.5 rounded-lg border border-[#D8C4A8] shadow-sm" data-id="element-3469">
                  <Languages className="w-4 h-4 text-[#2F4A3F]" data-id="element-3470" />
                  Русский язык
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4" data-id="element-3471">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-3472">
                  <Button variant="primary" size="lg" className="w-full gap-2" data-id="element-3473">
                    <Calendar className="w-5 h-5" data-id="element-3474" />
                    Записаться на приём
                  </Button>
                </a>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-3475">
                  <Button variant="whatsapp" size="lg" className="w-full gap-2" data-id="element-3476">
                    <MessageCircle className="w-5 h-5" data-id="element-3477" />
                    WhatsApp
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative" data-id="element-3478">
              <div className="absolute inset-0 bg-[#D8C4A8] rounded-3xl transform rotate-3 blur-sm opacity-30" data-id="element-3479"></div>
              <img src="https://images.unsplash.com/photo-1598256989800-fe5f95da9787?auto=format&fit=crop&w=1200&q=80" alt="Лечение каналов - Dentico Clinic" className="relative rounded-3xl shadow-2xl object-cover h-[300px] sm:h-[400px] lg:h-[500px] w-full" data-id="element-3480" />
            </div>
          </div>
        </div>
      </section>

      {/* 2. WHAT IS ROOT CANAL */}
      <section className="py-16 bg-white" data-id="element-3481">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3482">
          <div className="text-center mb-10" data-id="element-3483">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-3484">
              Что такое лечение каналов?
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed" data-id="element-3485">
              Лечение корневых каналов (эндодонтия) — это процедура,
              направленная на спасение зуба, который был сильно поврежден
              кариесом или инфекцией. Когда бактерии проникают внутрь зуба к
              нерву (пульпе), это вызывает воспаление и боль. Если не провести
              лечение вовремя, инфекция может распространиться на кость и
              привести к потере зуба.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mt-4" data-id="element-3486">
              Современная эндодонтия позволяет очистить каналы от инфекции,
              запломбировать их и восстановить зуб, сохраняя его
              функциональность на долгие годы. Это единственная альтернатива
              удалению зуба при глубоком поражении.
            </p>
          </div>
        </div>
      </section>

      {/* 3. PROCESS */}
      <section className="py-16 bg-gray-50" data-id="element-3487">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3488">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-3489">
            Как проходит лечение
          </h2>
          <div className="grid md:grid-cols-5 gap-4" data-id="element-3490">
            {[{
            step: '01',
            title: 'Диагностика',
            desc: 'Рентген-снимок для оценки состояния каналов.'
          }, {
            step: '02',
            title: 'Анестезия',
            desc: 'Полное обезболивание для комфортного лечения.'
          }, {
            step: '03',
            title: 'Очистка',
            desc: 'Удаление инфицированных тканей и обработка каналов.'
          }, {
            step: '04',
            title: 'Пломбирование',
            desc: 'Герметичное заполнение каналов специальным материалом.'
          }, {
            step: '05',
            title: 'Восстановление',
            desc: 'Установка пломбы или коронки для защиты зуба.'
          }].map((item, idx) => <Card key={idx} className="p-5 relative overflow-hidden h-full" data-id="element-3491">
                <div className="text-5xl font-bold text-[#EFE8DE] absolute -top-2 -right-2 select-none" data-id="element-3492">
                  {item.step}
                </div>
                <div className="relative z-10" data-id="element-3493">
                  <h3 className="text-lg font-bold text-[#2B2D2F] mb-2" data-id="element-3494">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-3495">{item.desc}</p>
                </div>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 4. MODERN APPROACH */}
      <section className="py-16 bg-white" data-id="element-3496">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3497">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-3498">
            <div data-id="element-3499">
              <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-3500">
                Современный подход к лечению
              </h2>
              <ul className="space-y-4" data-id="element-3501">
                {['Использование дентального микроскопа для точности', 'Цифровая рентген-диагностика (минимальное облучение)', 'Эффективная анестезия (лечение без боли)', 'Возможность лечения за один визит', 'Сохранение даже сложных зубов'].map((item, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-3502">
                    <CheckCircle2 className="w-6 h-6 text-forest-600 flex-shrink-0 mt-0.5" data-id="element-3503" />
                    <span className="text-gray-700 text-lg" data-id="element-3504">{item}</span>
                  </li>)}
              </ul>
              <div className="mt-8" data-id="element-3505">
                <Link to="/ru/contact" data-id="element-3506">
                  <Button size="lg" className="gap-2 !opacity-100 !bg-[#2F4A3F] !text-white !border !border-[#2F4A3F] hover:!bg-[#3A5A4D]" data-id="element-3507">
                    <Calendar className="w-5 h-5" data-id="element-3508" />
                    Записаться на консультацию
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative h-[400px] rounded-2xl overflow-hidden shadow-xl" data-id="element-3509">
              <img src="https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=1200&q=80" alt="Современное лечение каналов" className="absolute inset-0 w-full h-full object-cover" data-id="element-3510" />
            </div>
          </div>
        </div>
      </section>

      {/* 5. BENEFITS */}
      <section className="py-16 bg-[#EFE8DE]" data-id="element-3511">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3512">
          <div className="text-center mb-12" data-id="element-3513">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-3514">
              Почему стоит лечить каналы у нас
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-3515">
            {[{
            title: 'Сохранение зуба',
            desc: 'Мы боремся за каждый зуб, чтобы избежать удаления и имплантации.'
          }, {
            title: 'Без боли',
            desc: 'Современные анестетики гарантируют комфорт во время процедуры.'
          }, {
            title: 'Русский язык',
            desc: 'Вы точно поймете диагноз и план лечения.'
          }, {
            title: 'Опытные врачи',
            desc: 'Специалисты по эндодонтии, работающие со сложными случаями.'
          }].map((item, idx) => <Card key={idx} className="p-6 bg-white text-center" data-id="element-3516">
                <div className="w-12 h-12 bg-[#F8F5F0] rounded-full flex items-center justify-center text-[#2F4A3F] mb-4 mx-auto border border-[#D8C4A8]" data-id="element-3517">
                  <ShieldCheck className="w-6 h-6" data-id="element-3518" />
                </div>
                <h3 className="text-lg font-bold text-[#2B2D2F] mb-2" data-id="element-3519">
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-3520">{item.desc}</p>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 6. FAQ */}
      <section className="py-16 bg-white" data-id="element-3521">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3522">
          <div className="text-center mb-10" data-id="element-3523">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-3524">
              Частые вопросы о лечении каналов
            </h2>
          </div>
          <div className="space-y-2" data-id="element-3525">
            {faqs.map((faq, idx) => <AccordionItem key={idx} question={faq.q} answer={faq.a} isOpen={openFaqIndex === idx} onToggle={() => toggleFaq(idx)} data-id="element-3526" />)}
          </div>
        </div>
      </section>

      {/* 7. INTERNAL LINKS */}
      <section className="py-12 bg-gray-50 border-t border-gray-100" data-id="element-3527">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3528">
          <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-3529">
            Смотрите также
          </h2>
          <div className="flex flex-wrap gap-4" data-id="element-3530">
            <Link to="/ru/russkij-stomatolog-london" data-id="element-3531">
              <Button variant="outline" size="sm" data-id="element-3532">
                Русскоязычный стоматолог
              </Button>
            </Link>
            <Link to="/ru/neotlozhnaya-stomatologiya-london" data-id="element-3533">
              <Button variant="outline" size="sm" data-id="element-3534">
                Неотложная помощь
              </Button>
            </Link>
            <Link to="/ru/services/crowns" data-id="element-3535">
              <Button variant="outline" size="sm" data-id="element-3536">
                Коронки
              </Button>
            </Link>
            <Link to="/ru/prices" data-id="element-3537">
              <Button variant="outline" size="sm" data-id="element-3538">
                Цены
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 8. CTA */}
      <section className="py-16 bg-forest-900" data-id="element-3539">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-3540">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-3541">
            Не терпите зубную боль
          </h2>
          <p className="text-xl text-ivory/80 mb-8 max-w-2xl mx-auto" data-id="element-3542">
            Сохраните свой зуб сегодня. Запишитесь на консультацию.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-3543">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-3544">
              <Button size="lg" className="w-full gap-2 !opacity-100 !bg-[#F8F5F0] !text-[#2F4A3F] !border !border-[#F8F5F0] hover:!bg-[#EFE8DE]" data-id="element-3545">
                <Calendar className="w-5 h-5" data-id="element-3546" />
                Записаться онлайн
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-3547">
              <Button size="lg" className="w-full gap-2 !opacity-100 !bg-[#EFE8DE] !text-[#2F4A3F] !border !border-[#D8C4A8] hover:!bg-[#2F4A3F] hover:!text-white" data-id="element-3548">
                <MessageCircle className="w-5 h-5" data-id="element-3549" />
                WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>
    </Layout>;
}